import type { CatalogFilters, TypeFilter } from '@/entities/catalog';
import type { HomeWidget } from '@/entities/viewer';

import { HOME_SORTS } from './home-filters';

/** The sections that list resources: everything the search and the filters act on. */
const RESOURCE_WIDGETS: readonly HomeWidget[] = [
  'workspaces',
  'groups',
  'boards',
  'databases',
  'documents',
];

export interface HomeScope {
  /** Any resource section is shown: the search has something to search. */
  searchable: boolean;
  /** A section the filters act on is shown (workspaces alone are only searched). */
  filterable: boolean;
  /** The resource types the visible sections can show; groups hold all three. */
  types: TypeFilter[];
  sorts: typeof HOME_SORTS | readonly [];
}

/**
 * What the contextual Home controls may offer for the sections the viewer left visible. The
 * controls follow the content: hide every resource section and no search, filter or sort remains.
 */
export function homeScope(hidden: readonly HomeWidget[]): HomeScope {
  const shown = (id: HomeWidget) => !hidden.includes(id);
  const groups = shown('groups');
  const types: TypeFilter[] = [];
  if (shown('boards') || groups) types.push('board');
  if (shown('databases') || groups) types.push('database');
  if (shown('documents') || groups) types.push('document');
  const lists = shown('boards') || shown('databases') || shown('documents');
  return {
    searchable: RESOURCE_WIDGETS.some(shown),
    filterable: lists || groups,
    types: types.length > 0 ? ['all', ...types] : [],
    sorts: lists ? HOME_SORTS : [],
  };
}

/** A stored type filter whose section is hidden does not narrow anything (and comes back when shown). */
export function effectiveFilters(filters: CatalogFilters, scope: HomeScope): CatalogFilters {
  return scope.types.includes(filters.type) ? filters : { ...filters, type: 'all' };
}
